// src/components/TaskEditModal.tsx
import React, { useState, useEffect } from "react";
import { 
  FiX, FiCalendar, FiUser, FiFlag, FiList,
  FiPlus, FiCheck, FiTrash2, FiEdit3
} from "react-icons/fi";
import { supabase } from "../../lib/supabase";
import type { Task, TaskStatus, Priority, Project } from "../../types/domain";
import "../TaskEditModal.css";

interface Label {
  id: string;
  name: string;
  color: string;
}

interface SubtaskUI {
  id: string;
  title: string;
  completed: boolean;
}

interface TaskEditModalProps {
  task: Task | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (task: Task) => Promise<void>;
  projects: Project[];
  showStatusField?: boolean;
  showDateField?: boolean;
  initialStatus?: TaskStatus;
}

const STATUS_OPTIONS: { value: TaskStatus; label: string }[] = [
  { value: 'backlog', label: 'Backlog' },
  { value: 'todo', label: 'Por hacer' },
  { value: 'in_progress', label: 'En progreso' },
  { value: 'in_review', label: 'En revisión' },
  { value: 'done', label: 'Hecho' },
];

const PRIORITY_OPTIONS: { value: Priority; label: string }[] = [
  { value: 'low', label: 'Baja' },
  { value: 'medium', label: 'Media' },
  { value: 'high', label: 'Alta' },
  { value: 'urgent', label: 'Urgente' },
];

export default function TaskEditModal({
  task,
  isOpen,
  onClose,
  onSave,
  projects,
  showStatusField = true,
  showDateField = true,
  initialStatus = 'todo'
}: TaskEditModalProps) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium' as Priority,
    status: 'todo' as TaskStatus,
    deadline: '',
    start_date: '',
    end_date: '',
    assignee_id: '',
    project_id: ''
  });
  
  const [labels, setLabels] = useState<Label[]>([]);
  const [selectedLabels, setSelectedLabels] = useState<Label[]>([]);
  const [subtasks, setSubtasks] = useState<SubtaskUI[]>([]);
  const [newSubtask, setNewSubtask] = useState('');
  const [newLabelName, setNewLabelName] = useState('');
  const [showCreateLabel, setShowCreateLabel] = useState(false);
  const [loading, setLoading] = useState(false);

  // Cargar datos de la tarea
  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || '',
        description: task.description || '',
        priority: task.priority || 'medium',
        status: task.status,
        deadline: task.deadline ? task.deadline.split('T')[0] : '',
        start_date: task.start_date ? task.start_date.split('T')[0] : '',
        end_date: task.end_date ? task.end_date.split('T')[0] : '',
        assignee_id: task.assignee_id || '',
        project_id: task.project_id || ''
      });
      
      // Cargar subtareas y etiquetas
      loadTaskDetails(task.id);
    } else {
      // Resetear formulario para nueva tarea
      setFormData({
        title: '',
        description: '',
        priority: 'medium',
        status: initialStatus,
        deadline: '',
        start_date: '',
        end_date: '',
        assignee_id: '',
        project_id: ''
      });
      setSelectedLabels([]);
      setSubtasks([]);
    }
  }, [task]);

  // Cargar etiquetas disponibles
  useEffect(() => {
    loadLabels();
  }, []);

  const loadLabels = async () => {
    try {
      const { data } = await supabase
        .from('labels')
        .select('*')
        .order('name');
      
      if (data) setLabels(data);
    } catch (error) {
      console.error('Error loading labels:', error);
    }
  };

  const loadTaskDetails = async (taskId: string) => {
    try {
      // Cargar etiquetas de la tarea
      const { data: taskLabels } = await supabase
        .from('task_labels')
        .select('label_id, labels(*)')
        .eq('task_id', taskId);

      if (taskLabels) {
        setSelectedLabels(taskLabels.map(tl => tl.labels).filter(Boolean));
      }

      // Cargar subtareas
      const { data: taskSubtasks } = await supabase
        .from('subtasks')
        .select('*')
        .eq('task_id', taskId)
        .order('created_at');

      if (taskSubtasks) {
        setSubtasks(taskSubtasks.map(st => ({
          id: st.id,
          title: st.title,
          completed: st.done
        })));
      }
    } catch (error) {
      console.error('Error loading task details:', error);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const toggleLabel = (label: Label) => {
    setSelectedLabels(prev => {
      const isSelected = prev.some(l => l.id === label.id);
      if (isSelected) {
        return prev.filter(l => l.id !== label.id);
      } else {
        return [...prev, label];
      }
    });
  };

  const addSubtask = () => {
    if (newSubtask.trim()) {
      const subtask: SubtaskUI = {
        id: `temp-${Date.now()}`,
        title: newSubtask.trim(),
        completed: false
      };
      setSubtasks(prev => [...prev, subtask]);
      setNewSubtask('');
    }
  };

  const toggleSubtask = (subtaskId: string) => {
    setSubtasks(prev => prev.map(st => 
      st.id === subtaskId ? { ...st, completed: !st.completed } : st
    ));
  };

  const removeSubtask = (subtaskId: string) => {
    setSubtasks(prev => prev.filter(st => st.id !== subtaskId));
  };

  const createLabel = async () => {
    if (!newLabelName.trim()) return;

    try {
      // Generate a random color for the new label
      const colors = ['#36B37E', '#0065FF', '#FF5630', '#FFAB00', '#6554C0', '#00B8D9'];
      const randomColor = colors[Math.floor(Math.random() * colors.length)];

      // Find the company_id from the selected project
      const selectedProject = projects.find(p => p.id === formData.project_id);
      if (!selectedProject) {
        console.error('No project selected or project not found');
        return;
      }

      const { data, error } = await supabase
        .from('labels')
        .insert({
          name: newLabelName.trim(),
          color: randomColor,
          company_id: selectedProject.company_id
        })
        .select()
        .single();

      if (error) throw error;

      const newLabel = data as Label;
      setLabels(prev => [...prev, newLabel]);
      setSelectedLabels(prev => [...prev, newLabel]);
      setNewLabelName('');
      setShowCreateLabel(false);
    } catch (error) {
      console.error('Error creating label:', error);
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim()) return;

    setLoading(true);
    try {
      const taskData: Partial<Task> = {
        ...formData,
        deadline: formData.deadline || null,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        assignee_id: formData.assignee_id || null,
      };

      if (task) {
        // Actualizar tarea existente
        const { error } = await supabase
          .from('tasks')
          .update(taskData)
          .eq('id', task.id);

        if (error) throw error;

        // Actualizar etiquetas
        await updateTaskLabels(task.id);
        // Actualizar subtareas
        await updateSubtasks(task.id);

        await onSave({ ...task, ...taskData } as Task);
      } else {
        // Crear nueva tarea
        const { data: newTask, error } = await supabase
          .from('tasks')
          .insert(taskData)
          .select()
          .single();

        if (error) throw error;

        // Añadir etiquetas y subtareas
        await updateTaskLabels(newTask.id);
        await updateSubtasks(newTask.id);

        await onSave(newTask);
      }

      onClose();
    } catch (error) {
      console.error('Error saving task:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateTaskLabels = async (taskId: string) => {
    // Eliminar etiquetas existentes
    await supabase
      .from('task_labels')
      .delete()
      .eq('task_id', taskId);

    // Añadir nuevas etiquetas
    if (selectedLabels.length > 0) {
      const taskLabels = selectedLabels.map(label => ({
        task_id: taskId,
        label_id: label.id
      }));

      await supabase
        .from('task_labels')
        .insert(taskLabels);
    }
  };

  const updateSubtasks = async (taskId: string) => {
    // Eliminar subtareas existentes
    await supabase
      .from('subtasks')
      .delete()
      .eq('task_id', taskId);

    // Añadir subtareas actuales
    if (subtasks.length > 0) {
      const subtaskData = subtasks.map(st => ({
        task_id: taskId,
        title: st.title,
        done: st.completed
      }));

      await supabase
        .from('subtasks')
        .insert(subtaskData);
    }
  };

  if (!isOpen) return null;

  const completedSubtasks = subtasks.filter(st => st.completed).length;
  const totalSubtasks = subtasks.length;

  return (
    <div className="task-modal-overlay" onClick={onClose}>
      <div className="task-modal" onClick={e => e.stopPropagation()}>
        <div className="task-modal-header">
          <h2>
            <FiEdit3 size={22} />
            {task ? 'Editar Tarea' : 'Nueva Tarea'}
          </h2>
          <button className="close-btn" onClick={onClose}>
            <FiX size={18} />
          </button>
        </div>

        <div className="task-modal-content">
          <div className="task-modal-main">
            {/* Título */}
            <div className="form-group">
              <label>Título</label>
              <input
                type="text"
                value={formData.title}
                onChange={e => handleInputChange('title', e.target.value)}
                placeholder="Escribe el título de la tarea..."
                className="form-input"
              />
            </div>

            {/* Descripción */}
            <div className="form-group">
              <label>Descripción</label>
              <textarea
                value={formData.description}
                onChange={e => handleInputChange('description', e.target.value)}
                placeholder="Describe la tarea..."
                rows={4}
                className="form-textarea"
              />
            </div>

            {/* Subtareas */}
            <div className="form-group">
              <label>
                Subtareas 
                {totalSubtasks > 0 && (
                  <span className="subtask-progress">
                    {completedSubtasks}/{totalSubtasks}
                  </span>
                )}
              </label>
              
              <div className="subtasks-container">
                {subtasks.map(subtask => (
                  <div key={subtask.id} className={`subtask-item ${subtask.completed ? 'completed' : ''}`}>
                    <button 
                      className={`subtask-checkbox ${subtask.completed ? 'checked' : ''}`}
                      onClick={() => toggleSubtask(subtask.id)}
                    >
                      {subtask.completed && <FiCheck size={12} />}
                    </button>
                    <span className="subtask-title">{subtask.title}</span>
                    <button 
                      className="subtask-remove"
                      onClick={() => removeSubtask(subtask.id)}
                    >
                      <FiTrash2 size={12} />
                    </button>
                  </div>
                ))}
                
                <div className="add-subtask">
                  <input
                    type="text"
                    value={newSubtask}
                    onChange={e => setNewSubtask(e.target.value)}
                    placeholder="Añadir subtarea..."
                    className="form-input"
                    onKeyPress={e => e.key === 'Enter' && addSubtask()}
                  />
                  <button 
                    className="add-subtask-btn"
                    onClick={addSubtask}
                    disabled={!newSubtask.trim()}
                  >
                    <FiPlus size={16} />
                  </button>
                </div>
              </div>
            </div>

            {/* Etiquetas */}
            <div className="form-group">
              <label>Etiquetas</label>
              <div className="labels-container">
                <div className="selected-labels">
                  {selectedLabels.map(label => (
                    <span 
                      key={label.id} 
                      className="selected-label"
                      style={{ backgroundColor: label.color }}
                      onClick={() => toggleLabel(label)}
                    >
                      {label.name}
                      <FiX size={12} />
                    </span>
                  ))}
                </div>
                
                <div className="available-labels">
                  {labels.filter(l => !selectedLabels.some(sl => sl.id === l.id)).map(label => (
                    <span 
                      key={label.id} 
                      className="available-label"
                      style={{ borderColor: label.color, color: label.color }}
                      onClick={() => toggleLabel(label)}
                    >
                      {label.name}
                    </span>
                  ))}
                  
                  {/* Create Label Button/Form */}
                  {!showCreateLabel ? (
                    <button
                      type="button"
                      className="create-label-btn"
                      onClick={() => setShowCreateLabel(true)}
                    >
                      <FiPlus size={14} />
                      Nueva etiqueta
                    </button>
                  ) : (
                    <div className="create-label-form">
                      <input
                        type="text"
                        className="create-label-input"
                        placeholder="Nombre de la etiqueta"
                        value={newLabelName}
                        onChange={e => setNewLabelName(e.target.value)}
                        onKeyPress={e => e.key === 'Enter' && createLabel()}
                        autoFocus
                      />
                      <div className="create-label-actions">
                        <button
                          type="button"
                          className="create-label-save"
                          onClick={createLabel}
                          disabled={!newLabelName.trim()}
                        >
                          <FiCheck size={14} />
                        </button>
                        <button
                          type="button"
                          className="create-label-cancel"
                          onClick={() => {
                            setShowCreateLabel(false);
                            setNewLabelName('');
                          }}
                        >
                          <FiX size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="task-modal-sidebar">
            {/* Prioridad */}
            <div className="form-group">
              <label>
                <FiFlag size={16} />
                Prioridad
              </label>
              <select
                value={formData.priority}
                onChange={e => handleInputChange('priority', e.target.value)}
                className="form-select"
              >
                {PRIORITY_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Estado (condicional) */}
            {showStatusField && (
              <div className="form-group">
                <label>
                  <FiList size={16} />
                  Estado
                </label>
                <select
                  value={formData.status}
                  onChange={e => handleInputChange('status', e.target.value)}
                  className="form-select"
                >
                  {STATUS_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Fechas (condicional) */}
            {showDateField && (
              <>
                <div className="form-group">
                  <label>
                    <FiCalendar size={16} />
                    Fecha de inicio
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={e => handleInputChange('start_date', e.target.value)}
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label>
                    <FiCalendar size={16} />
                    Fecha de fin
                  </label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={e => handleInputChange('end_date', e.target.value)}
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label>
                    <FiCalendar size={16} />
                    Fecha límite
                  </label>
                  <input
                    type="date"
                    value={formData.deadline}
                    onChange={e => handleInputChange('deadline', e.target.value)}
                    className="form-input"
                  />
                </div>
              </>
            )}

            {/* Asignado a */}
            <div className="form-group">
              <label>
                <FiUser size={16} />
                Asignado a
              </label>
              <input
                type="text"
                value={formData.assignee_id}
                onChange={e => handleInputChange('assignee_id', e.target.value)}
                placeholder="ID del usuario"
                className="form-input"
              />
            </div>

            {/* Proyecto */}
            <div className="form-group">
              <label>Proyecto</label>
              <select
                value={formData.project_id}
                onChange={e => handleInputChange('project_id', e.target.value)}
                className="form-select"
              >
                <option value="">Seleccionar proyecto</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="task-modal-footer">
          <button 
            className="btn-primary"
            onClick={handleSave}
            disabled={loading || !formData.title.trim()}
          >
            {loading ? 'Guardando...' : (task ? 'Actualizar' : 'Crear')}
          </button>
          <button className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}